import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import Seo, { SITE_ORIGIN } from "@/components/aoria/Seo";
import { ARTICLES, getArticle } from "@/lib/articles";

// Convertit « Avril 2026 » → ISO « 2026-04-01 » pour les balises article:published_time
const MONTHS = {
    janvier: "01", "février": "02", mars: "03", avril: "04", mai: "05",
    juin: "06", juillet: "07", "août": "08", septembre: "09",
    octobre: "10", novembre: "11", "décembre": "12",
};
function toISODate(label) {
    const m = label.toLowerCase().match(/(\p{L}+)\s+(\d{4})/u);
    if (!m) return undefined;
    const month = MONTHS[m[1]];
    return month ? `${m[2]}-${month}-01` : undefined;
}

export default function Article() {
    const { slug } = useParams();
    const article = getArticle(slug);

    if (!article) {
        return (
            <PageLayout testid="article-not-found">
                <Seo
                    title="Ressource introuvable · Aoria"
                    description="Cette ressource d'Aoria n'existe pas ou n'est plus disponible."
                    path="/ressources"
                />
                <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-20 pb-32 text-center">
                    <span className="eyebrow">404, Ressource introuvable</span>
                    <h1 className="h1 mt-6 text-4xl md:text-5xl text-[#0D1B2A]">
                        Cette page n'existe pas, ou plus.
                    </h1>
                    <p className="mt-6 text-[#5C6B7D]">
                        Le lien que vous avez suivi ne correspond à aucune
                        publication d'Aoria.
                    </p>
                    <Link
                        to="/#ressources"
                        className="btn-primary inline-block mt-10 px-6 py-3 text-sm tracking-[0.15em] uppercase"
                    >
                        Voir les ressources
                    </Link>
                </div>
            </PageLayout>
        );
    }

    const others = ARTICLES.filter((a) => a.slug !== article.slug);
    const publishedAt = toISODate(article.date);
    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        author: { "@type": "Organization", name: "Aoria" },
        publisher: {
            "@type": "Organization",
            name: "Aoria",
            logo: {
                "@type": "ImageObject",
                url: `${SITE_ORIGIN}/aoria-logo-black.png`,
            },
        },
        datePublished: publishedAt,
        inLanguage: "fr-FR",
        mainEntityOfPage: `${SITE_ORIGIN}/ressources/${article.slug}`,
        image: article.img,
    };

    return (
        <PageLayout testid={`article-page-${article.slug}`}>
            <Seo
                title={`${article.title} · Aoria`}
                description={article.excerpt}
                path={`/ressources/${article.slug}`}
                type="article"
                publishedAt={publishedAt}
                image={`/og/${article.slug}.png`}
            />
            <script
                type="application/ld+json"
                // JSON-LD requires raw JSON in a <script>. We escape `<` to
                // `\u003c` so a field value containing `</script>` cannot
                // close the tag early — defence-in-depth XSS hardening.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(articleJsonLd).replace(
                        /</g,
                        "\\u003c"
                    ),
                }}
            />
            <article className="max-w-3xl mx-auto px-6 md:px-12 lg:px-20">
                <Link
                    to="/ressources"
                    className="text-xs tracking-[0.18em] uppercase text-[#5C6B7D] link-underline"
                >
                    ← Retour aux ressources
                </Link>

                <header className="mt-10">
                    <div className="flex items-center justify-between eyebrow">
                        <span>{article.category}</span>
                        <span className="text-[#5C6B7D]/70 text-[10px] tracking-[0.18em]">
                            {article.date}
                        </span>
                    </div>
                    <h1
                        data-testid="article-title"
                        className="h1 mt-6 text-4xl md:text-5xl lg:text-6xl text-[#0D1B2A] leading-[1.05]"
                    >
                        {article.title}
                    </h1>
                    <p className="mt-8 text-[#5C6B7D] text-xs tracking-[0.18em] uppercase">
                        Lecture · {article.readTime}
                    </p>
                </header>

                <div className="mt-14 aspect-[16/9] overflow-hidden">
                    <img
                        src={article.img}
                        alt={`Illustration de l'article : ${article.title}`}
                        width="940"
                        height="529"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <p
                    data-testid="article-lead"
                    className="mt-14 font-serif text-2xl md:text-3xl leading-[1.4] text-[#0D1B2A]"
                >
                    {article.lead}
                </p>

                <div className="mt-14 space-y-14">
                    {article.sections.map((s, i) => (
                        <section key={s.h} data-testid={`article-section-${i}`}>
                            <h2 className="h2 text-2xl md:text-3xl text-[#0D1B2A]">
                                <span className="editorial-num mr-3 text-[#778DA9]">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {s.h}
                            </h2>
                            <div className="mt-6 space-y-5">
                                {s.p.map((para) => (
                                    <p
                                        key={para}
                                        className="text-[#1B263B] text-base md:text-lg leading-relaxed"
                                    >
                                        {para}
                                    </p>
                                ))}
                            </div>
                            {s.subsections && s.subsections.length > 0 && (
                                <div className="mt-10 space-y-8 border-l border-[#E0E1DD] pl-6 md:pl-8">
                                    {s.subsections.map((sub) => (
                                        <div key={sub.h}>
                                            <h3 className="h3 text-lg md:text-xl text-[#0D1B2A]">
                                                {sub.h}
                                            </h3>
                                            <p className="mt-3 text-[#1B263B] text-base md:text-[1.0625rem] leading-relaxed">
                                                {sub.p}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    ))}
                </div>

                {article.conclusion && (
                    <section
                        data-testid="article-conclusion"
                        className="mt-20 border-t border-[#E0E1DD] pt-10"
                    >
                        <p className="eyebrow">En conclusion</p>
                        <p className="mt-5 font-serif text-2xl md:text-[1.75rem] leading-[1.35] text-[#0D1B2A]">
                            {article.conclusion}
                        </p>
                    </section>
                )}

                <nav
                    data-testid="article-internal-links"
                    aria-label="Liens utiles"
                    className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-3"
                >
                    <Link
                        to="/"
                        className="border border-[#E0E1DD] hover:border-[#0D1B2A] transition-colors px-5 py-4"
                    >
                        <span className="eyebrow text-[#778DA9]">Accueil</span>
                        <div className="mt-2 font-serif text-lg text-[#0D1B2A]">
                            Aoria
                        </div>
                    </Link>
                    <Link
                        to="/a-propos"
                        className="border border-[#E0E1DD] hover:border-[#0D1B2A] transition-colors px-5 py-4"
                    >
                        <span className="eyebrow text-[#778DA9]">À propos</span>
                        <div className="mt-2 font-serif text-lg text-[#0D1B2A]">
                            L'origine du projet
                        </div>
                    </Link>
                    <Link
                        to="/#manifeste"
                        className="border border-[#E0E1DD] hover:border-[#0D1B2A] transition-colors px-5 py-4"
                    >
                        <span className="eyebrow text-[#778DA9]">Manifeste</span>
                        <div className="mt-2 font-serif text-lg text-[#0D1B2A]">
                            Six articles fondateurs
                        </div>
                    </Link>
                </nav>

                <hr className="my-20 border-[#E0E1DD]" />

                <div className="text-sm text-[#5C6B7D]">
                    Article publié par Aoria, Association loi 1901. Pour
                    citer, contacter ou échanger sur cette publication,{" "}
                    <Link
                        to="/#participer"
                        className="text-[#0D1B2A] link-underline"
                    >
                        écrivez-nous
                    </Link>
                    .
                </div>
            </article>

            <section className="mt-28 md:mt-36 pt-20 border-t border-[#E0E1DD] bg-[#F4F5F7] pb-24 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                    <span className="eyebrow">Autres publications</span>
                    <h2 className="h2 mt-6 text-3xl md:text-4xl text-[#0D1B2A]">
                        Poursuivre la lecture.
                    </h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {others.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/ressources/${a.slug}`}
                                data-testid={`other-article-${a.slug}`}
                                className="group border border-[#E0E1DD] bg-[#FDFDFC] p-8 hover:border-[#0D1B2A] transition-colors flex flex-col"
                            >
                                <div className="flex items-center justify-between eyebrow">
                                    <span>{a.category}</span>
                                    <span className="text-[#5C6B7D]/70 text-[10px] tracking-[0.18em]">
                                        {a.date}
                                    </span>
                                </div>
                                <h3 className="h3 text-xl md:text-2xl text-[#0D1B2A] mt-4 leading-snug">
                                    {a.title}
                                </h3>
                                <span className="mt-6 text-sm text-[#0D1B2A] link-underline self-start">
                                    Lire l'article →
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
